import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { uploadContent } from "../../../api/action/user";

const PersonalInfo = ({ user, disabled, handleChange, handleProfileImgChange, profileImageToUpload }) => {
  const companyState = useSelector((state) => state.companyReducer.company);

  const [openPersonalInfo, setOpenPersonalInfo] = useState(true);
  const [profileImg, setProfileImg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const companyOperatingLocation = companyState?.operatingLocation?.code;
  const [countryCode, setCountryCode] = useState("");
  const [phoneFormat, setPhoneFormat] = useState({
    pattern: "",
    placeholder: "",
    maxLength: 15
  });

  useEffect(() => {
    if (companyOperatingLocation === "IND") {
      setCountryCode("+91");
      setPhoneFormat({
        pattern: "^[0-9]{10}$",
        placeholder: "98765 43210",
        maxLength: 10
      });
    } else if (companyOperatingLocation === "US") {
      setCountryCode("+1");
      setPhoneFormat({
        pattern: "^[0-9]{10}$",
        placeholder: "(555) 123-4567",
        maxLength: 10
      });
    } else {
      // Default to international format
      setCountryCode("");
      setPhoneFormat({
        pattern: "^[0-9+\\-\\(\\)\\s]{6,15}$",
        placeholder: "+XX XXX XXX XXXX",
        maxLength: 15
      });
    }
  }, [companyOperatingLocation]);

  useEffect(() => {
    if (user) {
      const profilePicture = user.profilePicture || {};
      const displayImage = profilePicture.displayLink || profilePicture.storedLink || '';
      setProfileImg(displayImage);
    }
  }, [user]);

  useEffect(() => {
    if (profileImageToUpload) {
      setPreviewUrl(profileImageToUpload.previewUrl);
    }
  }, [profileImageToUpload]);

  const statusMap = {
    ACTIVE: { color: "text-green-600", bgColor: "bg-green-50", label: "Active" },
    INACTIVE: { color: "text-red-600", bgColor: "bg-red-50", label: "Inactive" },
    EMAIL_VERIFIED: { color: "text-blue-600", bgColor: "bg-blue-50", label: "Verified" },
    NEW: { color: "text-amber-600", bgColor: "bg-amber-50", label: "New" },
    APPLIED: { color: "text-indigo-600", bgColor: "bg-indigo-50", label: "Applied" },
    ADMITTED: { color: "text-green-600", bgColor: "bg-green-50", label: "Admitted" },
  };

  const statusInfo = statusMap[user?.status] || statusMap.NEW;

  const handleProfilePictureChange = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];

      if (!file) throw new Error('No file selected');

      const previewUrl = URL.createObjectURL(file);
      setPreviewUrl(previewUrl);

      const companyFolderId = companyState?.dynamicAttributes?.[0]?.value;
      const uploadResponse = await uploadContent(file, companyFolderId);

      let imageUrl = '';
      if (typeof uploadResponse === 'string') {
        imageUrl = uploadResponse;
      } else if (uploadResponse?.displayLink) {
        imageUrl = uploadResponse.displayLink;
      } else if (uploadResponse?.storedLink) {
        imageUrl = uploadResponse.storedLink;
      } else if (uploadResponse?.url) {
        imageUrl = uploadResponse.url;
      }

      if (handleProfileImgChange) {
        handleProfileImgChange({
          storedLink: imageUrl,
          displayLink: imageUrl,
          previewUrl: previewUrl
        });
      }
    } catch {
      setPreviewUrl('');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = () => {
    setPreviewUrl("");
    if (handleProfileImgChange) {
      handleProfileImgChange({
        storedLink: "",
        displayLink: "",
        previewUrl: ""
      });
    }
  };

  const formatPhoneNumberForDisplay = (number) => {
    if (!number) return '';
    const cleaned = number.replace(/\D/g, '');

    if (companyOperatingLocation === "US") {
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
    } else if (companyOperatingLocation === "IND") {
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
      } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
        return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
      }
    }

    // Default international format
    return number;
  };

  const formatPhoneNumberForInput = (number) => {
    if (!number) return '';
    // Remove all non-digits for input field
    return number.replace(/\D/g, '');
  };

  const handlePhoneChange = (e) => {
    // Remove all non-digits for storage
    const numericValue = e.target.value.replace(/\D/g, '');

    // Create a synthetic event to pass to parent handler
    const syntheticEvent = {
      target: {
        name: "phoneNumber",
        value: numericValue
      }
    };

    handleChange(syntheticEvent);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (e) {
      return dateString;
    }
  };

  const displayImage = previewUrl || profileImg;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Personal Information</h2>
              <p className="text-sm text-gray-500">Your basic profile details</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg ${statusInfo.bgColor}`}>
              <div className={`w-2 h-2 rounded-full ${statusInfo.color.replace('text-', 'bg-')}`}></div>
              <span className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
            </div>

            <button
              type="button"
              onClick={() => setOpenPersonalInfo(!openPersonalInfo)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className={`w-5 h-5 transition-transform duration-200 ${openPersonalInfo ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {openPersonalInfo && (
        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Avatar Section */}
            <div className="lg:w-1/4">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-2 border-gray-200">
                    {displayImage ? (
                      <img
                        className="w-full h-full object-cover"
                        src={displayImage}
                        alt="Profile"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>

                  {!disabled && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-xl transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a1 1 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  )}
                </div>

                {!disabled && (
                  <div className="flex gap-2 w-full">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfilePictureChange}
                        disabled={uploading}
                      />
                      <div className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer disabled:opacity-50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        <span>Upload</span>
                      </div>
                    </label>

                    {(displayImage || previewUrl) && (
                      <button
                        type="button"
                        onClick={handleDeleteProfilePicture}
                        disabled={uploading}
                        className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                {uploading && (
                  <div className="flex items-center space-x-2 text-blue-600 text-sm">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Uploading...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Form Fields Section */}
            <div className="lg:w-3/4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  {disabled ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-gray-900">
                        {user.firstName || user.firstname || 'Not specified'}
                      </p>
                    </div>
                  ) : (
                    <input
                      type="text"
                      name="firstName"
                      value={user.firstName || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter first name"
                    />
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  {disabled ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-gray-900">
                        {user.lastName || user.lastname || 'Not specified'}
                      </p>
                    </div>
                  ) : (
                    <input
                      type="text"
                      name="lastName"
                      value={user.lastName || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter last name"
                    />
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center space-x-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-900 lowercase">
                      {user.email || 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  {disabled ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <div className="flex items-center">
                          {countryCode && (
                            <span className="text-gray-500 mr-1">{countryCode}</span>
                          )}
                          <p className="text-gray-900">
                            {formatPhoneNumberForDisplay(user.phoneNumber || user.phone) || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      {countryCode && (
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
                          <span className="text-gray-500">{countryCode}</span>
                          <div className="w-px h-5 bg-gray-300 mx-2"></div>
                        </div>
                      )}
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formatPhoneNumberForInput(user.phoneNumber || '')}
                        onChange={handlePhoneChange}
                        pattern={phoneFormat.pattern}
                        maxLength={phoneFormat.maxLength}
                        className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${countryCode ? 'pl-20' : ''}`}
                        placeholder={phoneFormat.placeholder}
                        title={countryCode ? `Format: ${countryCode} ${phoneFormat.placeholder}` : 'Enter phone number'}
                      />
                    </div>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  {disabled ? (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-900">
                        {user.dob ? new Date(user.dob).toLocaleDateString() : 'Not specified'}
                      </p>
                    </div>
                  ) : (
                    <input
                      type="date"
                      name="dob"
                      value={formatDateForInput(user.dob)}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  )}
                </div>

                {/* Created Date (Read-only) */}
                {user.createdDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Member Since
                    </label>
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-gray-900">
                        {new Date(user.createdDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfo;
